/**
 * Redux selectors for the backup config. Split out of `backup-config.ts` so
 * that module stays PURE (no reducer/analytics chain) and its resolvers can be
 * unit-tested without the app-config env. These selectors read the live user
 * document, so they import the reducer layer.
 */
import type { CommonRootState } from '@common/types/common-root-state-type';
import { selectUser } from '@common/reducers/UserSelectors';
import {
  resolveEffectiveBackupConfig,
  type RawBackupConfig,
  type ResolvedBackupConfig,
} from './backup-config';
import { getCachedBackendBackup } from './backup-config-source';

/** The user's raw `backup`, from the Redux store when present, else the
 *  backend-fetched cache (engine content stores don't always carry it —
 *  see backup-config-source). Prefer whichever has an explicit
 *  `dbBackupEnabled` so a store user that lacks the sub-doc doesn't mask an
 *  enabled backend config. */
const rawBackup = (state: CommonRootState): RawBackupConfig | undefined => {
  const fromStore = selectUser(state)?.backup as RawBackupConfig | undefined;
  if (fromStore && typeof fromStore.dbBackupEnabled === 'boolean') return fromStore;
  return (getCachedBackendBackup() as RawBackupConfig | null) ?? fromStore ?? undefined;
};

/** The config actually in force for THIS runtime (extension vs gateway + test
 *  override). The scheduler/uploader use this. */
export const selectEffectiveBackupConfig = (
  state: CommonRootState,
): ResolvedBackupConfig => resolveEffectiveBackupConfig(rawBackup(state));

/** True when message capture into the vault is enabled for the active context.
 *  Gates ALL capture paths (design: `vaultEnabled: false` → nothing captures,
 *  which also disables deleted-message recovery). Defaults enabled. */
export const isVaultCaptureEnabled = (state: CommonRootState): boolean =>
  selectEffectiveBackupConfig(state).vaultEnabled;
